// src/features/passwords/hooks/usePasswordEditing.js
import { useCallback, useMemo, useState } from 'react';
import api from '../../../api';

/**
 * Editing logic for Password / AdvicePassword pages.
 *
 * @param {Object} opts
 * @param {string} opts.resourcePrefix   '/passwords' or '/advice-passwords'
 * @param {Function} opts.refresh        callback to re-fetch all data
 * @param {boolean} [opts.withMultilogin=false] include multilogin handling (main Password page)
 * @param {Function} [opts.onNewRecordSuccess]   optional callback(userCode) after single-row create; when set, list refetch is driven by parent (e.g. set search to user)
 */
export default function usePasswordEditing({
  resourcePrefix,
  refresh,
  withMultilogin = false,
  onNewRecordSuccess,
}) {
  if (!resourcePrefix) {
    throw new Error('usePasswordEditing: resourcePrefix is required');
  }
  if (typeof refresh !== 'function') {
    throw new Error('usePasswordEditing: refresh (callback) is required');
  }

  // ---------- shared UI state ----------
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  // ---------- pending updates tracker (for realtime collision avoidance) ----------
  const [pendingUpdates, setPendingUpdates] = useState(() => new Set());

  // ---------- child-row editing ----------
  const emptyEditDraft = useMemo(
    () => ({
      oldFullName: '',
      fullName: '',
      email: '',
      site: '',
      password: '',
      address: '',
      dob: '',
      email_specific_name: '',
      email_specific_dob: '',
      email_specific_address: '',
      proxy: '',  // ✅ NEW: Add proxy field
      user_managed_numbers: [],  // User-managed phone numbers
    }),
    []
  );
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyEditDraft);

  // Address editor (inline widget on the row)
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const [addressParts, setAddressParts] = useState({
    line1: '',
    line2: '',
    town: '',
    county: '',
    postcode: '',
    country: '',
  });

  const parseAddressIntoParts = useCallback((fullAddress) => {
    if (!fullAddress) {
      return { line1: '', line2: '', town: '', county: '', postcode: '', country: '' };
    }
    const parts = String(fullAddress).split(',');
    return {
      line1: parts[0]?.trim() || '',
      line2: parts[1]?.trim() || '',
      town: parts[2]?.trim() || '',
      county: parts[3]?.trim() || '',
      postcode: parts[4]?.trim() || '',
      country: parts[5]?.trim() || '',
    };
  }, []);

  const handleAddressSave = useCallback(() => {
    const combined = [
      addressParts.line1,
      addressParts.line2,
      addressParts.town,
      addressParts.county,
      addressParts.postcode,
      addressParts.country,
    ]
      .filter(Boolean)
      .join(', ');
    setEditDraft((prev) => ({ ...prev, address: combined }));
    setShowAddressEditor(false);
  }, [addressParts]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toISOString().split('T')[0];
    } catch {
      return null;
    }
  }, []);

  /**
   * Begin editing a child password row.
   * @param {Object} entry - row from enrichedPasswords
   * @param {Object} usersMap - { [user_code]: { base: {...}, [email]: {...} } }
   */
  const startEditingRow = useCallback((entry, usersMap) => {
    setEditingId(entry.id);
    const perUser = usersMap[entry.user_code] || {};
    const baseRow = perUser.base || {};
    const emailRow = perUser[entry.email] || {};

    const fullName =
      emailRow.email_specific_name ||
      baseRow.email_specific_name ||
      baseRow.full_name ||
      '';

    const address =
      emailRow.email_specific_address ||
      baseRow.email_specific_address ||
      baseRow.address ||
      '';

    const dob =
      emailRow.email_specific_dob ||
      baseRow.email_specific_dob ||
      baseRow.dob ||
      '';

    setEditDraft({
      oldFullName: baseRow.full_name || '',
      fullName,
      email: entry.email || '',
      site: entry.site || '',
      password: entry.password || '',
      address,
      dob,
      email_specific_name: emailRow.email_specific_name || '',
      email_specific_dob: emailRow.email_specific_dob || '',
      email_specific_address: emailRow.email_specific_address || '',
      proxy: entry.proxy || '',  // ✅ NEW: Initialize proxy from entry
      user_managed_numbers: entry.user_managed_numbers || [],  // User-managed phone numbers
    });

    setAddressParts(parseAddressIntoParts(address));
    setShowAddressEditor(false);
  }, [parseAddressIntoParts]);

  const changeEditField = useCallback((field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const cancelEditingRow = useCallback(() => {
    setEditingId(null);
    setEditDraft({
      oldFullName: '',
      fullName: '',
      email: '',
      site: '',
      password: '',
      address: '',
      dob: '',
      email_specific_name: '',
      email_specific_dob: '',
      email_specific_address: '',
      proxy: '',  // ✅ NEW: Clear proxy on cancel
      user_managed_numbers: [],
    });
    setShowAddressEditor(false);
  }, []);

  /**
   * Save a single child row edit.
   */
  const saveEditingRow = useCallback(
    async (entry) => {
      try {
        setError(null);

        // 1) Update password row
        await api.put(`${resourcePrefix}/${entry.id}`, {
          id: entry.id,
          user_code: entry.user_code,
          email: editDraft.email,
          site: editDraft.site,
          password: editDraft.password,
          main: entry.main,
          user_managed_numbers: editDraft.user_managed_numbers || [],
        });

        // 2) Update per-email user fields
        const updatePayload = {
          email: editDraft.email,
          email_specific_name: editDraft.fullName || null,
          email_specific_dob: formatDate(editDraft.dob),
          email_specific_address: editDraft.address || null,
        };
        await api.put(`/users/${entry.user_code}`, updatePayload);

        showToast('Changes saved!');
        cancelEditingRow();
        await refresh();
      } catch (err) {
        console.error('saveEditingRow error:', err);
        const detail = err?.response?.data?.detail || 'Failed to save changes';
        setError(typeof detail === 'string' ? detail : 'Failed to save changes');
      }
    },
    [editDraft, resourcePrefix, formatDate, refresh, cancelEditingRow, showToast]
  );

  /**
   * Save child row AND apply key fields to all emails for that user.
   * @param {Object} entry - row
   * @param {Object} [opts]
   * @param {boolean} [opts.multilogin] - only used when withMultilogin=true
   */
  const saveAndApplyAll = useCallback(
    async (entry, opts = {}) => {
      const { multilogin } = opts;
      try {
        setError(null);
        setPendingUpdates((prev) => new Set(prev).add(entry.user_code));

        // 1) Update the specific password row first
        await api.put(`${resourcePrefix}/${entry.id}`, {
          id: entry.id,
          user_code: entry.user_code,
          email: editDraft.email,
          site: editDraft.site,
          password: editDraft.password,
          main: entry.main,
        });

        // 2) Update base user (so future lookups use base)
        const newFullName = (editDraft.fullName || '').trim();
        await api.put(`/users/${entry.user_code}`, {
          email: null,
          full_name: newFullName || null,
          DOB: formatDate(editDraft.dob),
          address: editDraft.address || null,
          ...(withMultilogin ? { multilogin: !!multilogin } : {}),
        });

        // 3) Ask backend to "apply" to all emails
        await api.put('/users/apply-changes', {
          user_code: entry.user_code,
          full_name: newFullName || null,
          address: editDraft.address || null,
          dob: formatDate(editDraft.dob),
          ...(withMultilogin ? { multilogin: !!multilogin } : {}),
        });

        showToast('Saved + Applied to all!');
        cancelEditingRow();
        await refresh();
      } catch (err) {
        console.error('saveAndApplyAll error:', err);
        const detail = err?.response?.data?.detail || 'Failed to apply changes';
        setError(typeof detail === 'string' ? detail : 'Failed to apply changes');
      } finally {
        setPendingUpdates((prev) => {
          const next = new Set(prev);
          next.delete(entry.user_code);
          return next;
        });
      }
    },
    [
      editDraft,
      resourcePrefix,
      formatDate,
      refresh,
      cancelEditingRow,
      showToast,
      withMultilogin,
    ]
  );

  // ---------- create/delete child records ----------
  const [addingNewRecord, setAddingNewRecord] = useState(false);
  const [newRecordUserCode, setNewRecordUserCode] = useState(null);
  const [newRecordDraft, setNewRecordDraft] = useState({
    email: '',
    site: '',
    password: '',
    address: '',
    dob: '',
    fullName: '',
  });

  const createNewRecord = useCallback(
    async (userCode) => {
      try {
        setError(null);

        // 1) Create password row
        const resp = await api.post(`${resourcePrefix}/`, {
          id: 0,
          user_code: userCode,
          email: newRecordDraft.email,
          site: newRecordDraft.site,
          password: newRecordDraft.password,
          main: false,
        });
        if (!resp?.data) {
          throw new Error('Failed to create password record');
        }

        // 2) Attach per-email user fields for that email
        await api.put(`/users/${userCode}`, {
          email: newRecordDraft.email,
          email_specific_name: newRecordDraft.fullName || null,
          email_specific_dob: newRecordDraft.dob
            ? new Date(newRecordDraft.dob).toISOString().split('T')[0]
            : null,
          email_specific_address: newRecordDraft.address || null,
        });

        showToast('New record created!');
        setAddingNewRecord(false);
        setNewRecordUserCode(null);
        setNewRecordDraft({
          email: '',
          site: '',
          password: '',
          address: '',
          dob: '',
          fullName: '',
        });
        if (onNewRecordSuccess) {
          onNewRecordSuccess(userCode);
        } else {
          await refresh();
        }
      } catch (err) {
        console.error('createNewRecord error:', err);
        setError('Failed to create new record');
      }
    },
    [newRecordDraft, resourcePrefix, refresh, showToast, onNewRecordSuccess]
  );

  const deleteRecord = useCallback(
    async (entry) => {
      const label = entry?.site || entry?.email || entry?.user_code || 'this record';
      if (!window.confirm(`Are you sure you want to delete "${label}"? This cannot be undone.`)) return;
      try {
        setError(null);
        await api.delete(`${resourcePrefix}/${entry.id}`);
        showToast('Record deleted!');
        await refresh();
      } catch (err) {
        console.error('deleteRecord error:', err);
        setError('Failed to delete record');
      }
    },
    [resourcePrefix, refresh, showToast]
  );

  // ---------- main row editing (user-level fields) ----------
  const [editingMainRow, setEditingMainRow] = useState(null);
  const [mainRowEditDraft, setMainRowEditDraft] = useState({
    fullName: '',
    slackId: '',
    referral: '',
    crm_password: '',
    multilogin: false,
  });

  const startEditingMainRow = useCallback((userDetails) => {
    setEditingMainRow(userDetails.userCode);
    setMainRowEditDraft({
      fullName: userDetails.fullName || '',
      slackId: userDetails.slackId || '',
      referral: userDetails.referral || '',
      crm_password: userDetails.crm_password || '',
      multilogin: !!userDetails.multilogin,
    });
  }, []);

  const saveMainRow = useCallback(
    async (userCode) => {
      try {
        setError(null);
        setPendingUpdates((prev) => new Set(prev).add(userCode));

        // 1) Update base user row
        await api.put(`/users/${userCode}`, {
          email: null,
          full_name: mainRowEditDraft.fullName,
          slack_user_id: mainRowEditDraft.slackId,
          referral: mainRowEditDraft.referral,
          crm_password: mainRowEditDraft.crm_password,
          ...(withMultilogin ? { multilogin: !!mainRowEditDraft.multilogin } : {}),
        });

        // 2) Sync Slack ID to all ports for that user
        const portsResp = await api.get('/ports/');
        const userPorts = (portsResp.data || []).filter((p) => p.user_code === userCode);
        await Promise.all(
          userPorts.map((port) =>
            api.put(`/ports/${port.id}`, {
              ...port,
              slack_user_id: mainRowEditDraft.slackId,
            })
          )
        );

        // 3) Apply to all user emails (name/slack + multilogin if applicable)
        await api.put('/users/apply-changes', {
          user_code: userCode,
          full_name: mainRowEditDraft.fullName,
          slack_user_id: mainRowEditDraft.slackId,
          ...(withMultilogin ? { multilogin: !!mainRowEditDraft.multilogin } : {}),
        });

        showToast('Saved + synced to ports!');
        setEditingMainRow(null);
        await refresh();
      } catch (err) {
        console.error('saveMainRow error:', err);
        setError('Failed to save changes');
      } finally {
        setPendingUpdates((prev) => {
          const next = new Set(prev);
          next.delete(userCode);
          return next;
        });
      }
    },
    [mainRowEditDraft, refresh, showToast, withMultilogin]
  );

  // ---------- notes ----------
  const saveNotes = useCallback(async (userCode, newNotes) => {
    try {
      setError(null);
      await api.put(`/users/${userCode}`, { notes: newNotes, email: null });
      showToast('Notes saved!');
    } catch (err) {
      console.error('saveNotes error:', err);
      setError('Failed to save notes');
    }
  }, [showToast]);

  // ---------- public API ----------
  return {
    // feedback
    error,
    toastMessage,
    showToast,

    // collisions
    pendingUpdates,

    // child-row edit
    editingId,
    editDraft,
    changeEditField,
    startEditingRow,
    cancelEditingRow,
    saveEditingRow,
    saveAndApplyAll,

    // address editor
    showAddressEditor,
    setShowAddressEditor,
    addressParts,
    setAddressParts,
    parseAddressIntoParts,
    handleAddressSave,

    // child create/delete
    addingNewRecord,
    setAddingNewRecord,
    newRecordUserCode,
    setNewRecordUserCode,
    newRecordDraft,
    setNewRecordDraft,
    createNewRecord,
    deleteRecord,

    // main row edit
    editingMainRow,
    setEditingMainRow,
    mainRowEditDraft,
    setMainRowEditDraft,
    startEditingMainRow,
    saveMainRow,

    // notes
    saveNotes,
  };
}